import { Fragment, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Pane, useMap } from "react-leaflet";
import { useLocation } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Layers } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { colorOf, fmtDate } from "@/lib/status";

const BASEMAPS = {
  dark: { label: "Dark", url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attr: "Tiles &copy; Esri" },
  osm: { label: "OSM", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "&copy; OpenStreetMap" },
  sat: { label: "Satelit", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attr: "Tiles &copy; Esri" },
};

const LABELS = {
  flight_id: "Flight ID", block_id: "Block ID", tanggal_akuisisi: "Tgl Akuisisi", status_preprocessing: "Pre Processing", status_foto: "Proses Foto", status_lidar: "Proses LiDAR", luas_km2: "Luas (ha)",
  grid_id: "Grid ID", status_2d: "Digitasi 2D", tgl_submit_2d: "Submit 2D", tgl_supervisi_2d: "Supervisi 2D", status_3d: "Pemodelan 3D", tgl_submit_3d: "Submit 3D", tgl_supervisi_3d: "Supervisi 3D",
  cp_id: "ID", tipe: "Tipe", status: "Status", koordinat: "Koordinat", elevasi: "Elevasi (m)", tanggal_ukur: "Tgl Ukur", bm_id: "ID", kondisi: "Kondisi", keterangan: "Keterangan",
};

const LAYERS = [
  // `flight_id` yang jadi judul popup/tooltip karena ia identitas baris: unik dan NOT NULL.
  // `block_id` turun jadi baris di daftar properti — popupHtml menyaring keluar `layer.idKey`,
  // dan block_id boleh berulang (satu blok banyak flight) bahkan NULL, jadi ia tidak bisa
  // menamai satu sortie.
  //
  // `pane`/`z` menetapkan urutan tumpuk secara eksplisit: Flight Plan SELALU di atas Grid Peta.
  // Tanpa pane sendiri semua vektor berbagi `overlayPane`, dan urutannya = urutan mount — yang
  // berubah tiap kali respons datang (empat request terpisah) dan tiap kali grid di-remount oleh
  // tombol 2D/3D (`key` GeoJSON memuat `gridBy`). Control Points di atas keduanya supaya titiknya
  // tidak tertutup poligon. Titik BM memakai L.marker, jadi sudah di `markerPane` (600).
  { key: "flight-plan", label: "Flight Plan", idKey: "flight_id", sub: "Flight Plan", color: "#0EA5E9", pane: "paneFlightPlan", z: 420 },
  { key: "grid-peta", label: "Grid Peta", idKey: "grid_id", sub: "Grid Peta", color: "#F59E0B", pane: "paneGridPeta", z: 410 },
  { key: "control-points", label: "Control Points", idKey: "cp_id", sub: "Control Point", color: "#06B6D4", pane: "paneControlPoints", z: 430 },
  { key: "titik-bm", label: "Titik BM", idKey: "bm_id", sub: "Benchmark", color: "#E11D48" },
];

const judulLayer = (layer, p) => p[layer.idKey] ?? "—";

const popupHtml = (layer, p) => {
  const rows = Object.entries(p).filter(([k]) => k !== layer.idKey).map(([k, v]) => {
    // `v ?? "—"` bukan `v || "—"`: 0 dan string kosong-nya sendiri tetap data, bukan ketiadaan data.
    const teks = v ?? "—";
    const val = /status|kondisi/.test(k) ? `<span class="pop-pill" style="color:${colorOf(v)};background:${colorOf(v)}22">${teks}</span>`
      : /tgl|tanggal/.test(k) ? fmtDate(v) : k === "luas_km2" && v != null ? Math.round(v * 100) : teks;
    return `<div class="pop-row"><span>${LABELS[k] || k}</span><span>${val}</span></div>`;
  }).join("");
  return `<div data-testid="map-popup"><div class="pop-sub">${layer.sub}</div><div class="pop-title">${judulLayer(layer, p)}</div>${rows}</div>`;
};

const styleFor = (key, gridBy) => (f) => {
  const p = f.properties;
  // Warna polygon flight dibaca dari ada-tidaknya tanggal akuisisi, bukan dari kolom status:
  // satu-satunya sumber kebenaran akuisisi sekarang tanggal itu.
  // `fp-garis`: karena flight kini di atas grid, isian tipisnya akan menelan klik ke grid di
  // bawahnya. Kelas ini (index.css) membuat flight hanya bisa diklik di garis tepinya.
  if (key === "flight-plan") return { color: colorOf(p.tanggal_akuisisi ? "Selesai" : "Belum"), weight: 2.5, dashArray: "6 4", fillOpacity: 0.05, className: "fp-garis" };
  return { color: "#0B0F17", weight: 1, fillColor: colorOf(gridBy === "2d" ? p.status_2d : p.status_3d), fillOpacity: 0.45 };
};

const pointToLayer = (key) => (f, latlng) => {
  const c = colorOf(f.properties.status);
  // `pane` harus disebut di sini: L.geoJSON tidak meneruskan opsinya ke layer buatan pointToLayer.
  if (key === "control-points") return L.circleMarker(latlng, { radius: 6, color: "#0B0F17", weight: 2, fillColor: c, fillOpacity: 1, pane: "paneControlPoints" });
  return L.marker(latlng, { icon: L.divIcon({ className: "", html: `<div class="bm-icon" style="background:${c};color:${c}"></div>`, iconSize: [14, 14] }) });
};

const LegendRow = ({ c, t }) => <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />{t}</div>;

// Denyut lewat kelas CSS, bukan setStyle: gaya awal tidak perlu dihitung ulang (grid bisa diwarnai
// status_2d atau status_3d menurut tombol gridBy) dan animasinya selesai sendiri, jadi tidak ada
// state gaya yang bisa tertinggal.
// Ini bergantung pada Leaflet memasang gaya Path lewat ATRIBUT presentasi SVG (`stroke="..."`),
// bukan inline style — properti CSS mengalahkan atribut, tapi kalah dari inline style. Sudah
// terbukti untuk poligon (user melihat denyutnya, 2026-10-19); kalau nanti objeknya diam lagi,
// curigai itu dulu: di DevTools, `<path>` yang diklik harus punya `stroke` di daftar atribut,
// bukan di `style="..."`.
// Warna dasar dibaca dari atribut `stroke` itu, jadi tidak ada warna yang diduplikasi.
const denyut = (el) => {
  el.style.setProperty("--warna-dasar", el.getAttribute("stroke") || "#F8FAFC");
  el.classList.remove("denyut");
  void el.offsetWidth;   // paksa style dihitung ulang: kelas yang sudah ada tidak akan memulai ulang animasinya
  el.classList.add("denyut");
  // TIDAK mengembalikan cleanup. Efek pemanggilnya berjalan lagi saat `data` berubah, dan keempat
  // layer datang sebagai empat respons terpisah — cleanup akan mencabut kelasnya beberapa
  // milidetik setelah dipasang, sebelum sempat tergambar (denyutnya memang tidak pernah terlihat).
  // Animasi dan timeout ini membatasi dirinya sendiri, jadi tidak ada yang perlu dibereskan.
  setTimeout(() => el.classList.remove("denyut"), 3000);
};

// `getBounds` cuma ada di Path poligon (dan Circle); CircleMarker dan Marker — dipakai control-points
// dan titik-bm — hanya punya `getLatLng`. Bounds satu titik nol area, dan `maxZoom` di fitBounds yang
// menahan supaya kamera tidak melompat ke zoom maksimum.
const kotak = (l) => l.getBounds?.() ?? L.latLngBounds([l.getLatLng(), l.getLatLng()]);

// Fokus dari tabel: ikon Peta di Tabular Flight/Grid/CP mengirim {layer, id} lewat router state.
// Anak MapContainer supaya dapat `useMap()` — API resmi react-leaflet, tidak bergantung pada
// perilaku internalnya soal apakah sub-layer per-fitur benar-benar terpasang di map.
const Sorot = ({ refs, data, visible, setVisible }) => {
  const map = useMap();
  const { state, key } = useLocation();
  const layer = state?.fokus?.layer;
  const id = state?.fokus?.id;
  // Satu denyut per entri history. Keempat layer datang sebagai empat respons terpisah, jadi efek
  // ini jalan berkali-kali saat halaman baru dibuka; tanpa penjaga, kamera dan popup melompat di
  // tiap kedatangan. `key` dari react-router berganti di tiap entri history, jadi mengklik baris
  // yang sama dua kali tetap memicu denyut lagi.
  // Sengaja TIDAK direset di cleanup: kalau direset, StrictMode di mode dev menjalankannya dua kali
  // dan denyutnya cuma kelihatan sekali — sama seperti sekarang, tapi tanpa pengulangan yang tak
  // diminta saat layer lain di-toggle. Bundel produksi tidak menggandakan efek.
  const sudah = useRef(null);

  useEffect(() => {
    // `in` sekaligus menutup dua kasus: tidak ada fokus sama sekali (`layer` undefined) dan
    // `layerKey` yang salah ketik di salah satu halaman. Keduanya harus diam, bukan diam-diam
    // menambah kunci sampah ke `visible`.
    if (!(layer in visible)) return;
    // Layer yang di-uncheck tidak punya sub-layer di peta. Hidupkan dulu lalu keluar: `visible`
    // ada di dependensi, jadi efek ini jalan lagi setelah GeoJSON-nya ter-mount.
    if (!visible[layer]) { setVisible((s) => ({ ...s, [layer]: true })); return; }
    const target = refs.current[`${layer}:${id}`];
    // `refs` diisi `onEachFeature`, jadi isinya otoritatif. Sengaja TIDAK ada cek `map.hasLayer`:
    // itu tergantung perilaku internal react-leaflet (kapan sub-layer benardar-benar terpasang),
    // dan kalau keliru jawabannya efek ini diam total tanpa jejak — persis kegagalan yang bikin
    // sorotan tidak pernah muncul.
    if (!target || sudah.current === key) return;
    sudah.current = key;
    map.invalidateSize();   // container baru di-mount oleh route ini
    map.fitBounds(kotak(target), { padding: [40, 40], maxZoom: 17 });
    target.openPopup();
    // CircleMarker bisa saja belum punya elemen SVG kalau peta belum pernah menggambar layer ini;
    // kamera dan popup sudah memberi petunjuk, jadi denyut yang hilang bukan kegagalan.
    const el = target.getElement();
    if (el) denyut(el);
  }, [map, refs, data, visible, setVisible, layer, id, key]);

  return null;
};

export default function GisPage() {
  const [data, setData] = useState({});
  const [visible, setVisible] = useState({ "flight-plan": true, "grid-peta": true, "control-points": true, "titik-bm": true });
  const [base, setBase] = useState("dark");
  const [gridBy, setGridBy] = useState("2d");
  // Sub-layer Leaflet per fitur, supaya fokus dari tabel bisa menemukan objeknya langsung.
  // Impor Leaflet ada di atas, jadi render pertama selalu lewat browser — ref ini tidak pernah
  // membeku dari render server.
  const refs = useRef({});

  useEffect(() => {
    LAYERS.forEach((l) => api.get(`/gis/${l.key}`).then((r) => setData((s) => ({ ...s, [l.key]: r.data }))).catch((e) => toast.error(errMsg(e))));
  }, []);

  return (
    <div className="relative h-[calc(100vh-57px)] lg:h-screen" data-testid="gis-page">
      <MapContainer center={[-7.005, 110.40]} zoom={12} className="h-full w-full" zoomControl={false} data-testid="gis-map">
        <TileLayer key={base} url={BASEMAPS[base].url} attribution={BASEMAPS[base].attr} />
        {LAYERS.map((l) => {
          const geo = visible[l.key] && data[l.key] && (
            <GeoJSON key={`${l.key}-${gridBy}`} data={data[l.key]}
              style={l.key === "flight-plan" || l.key === "grid-peta" ? styleFor(l.key, gridBy) : undefined}
              pointToLayer={pointToLayer(l.key)}
              onEachFeature={(f, layer) => {
                refs.current[`${l.key}:${f.properties[l.idKey]}`] = layer;
                layer.bindPopup(popupHtml(l, f.properties));
                layer.bindTooltip(judulLayer(l, f.properties), { direction: "top", className: "font-mono" });
              }} />
          );
          // Pane selalu ter-mount (hanya GeoJSON-nya yang kondisional), jadi z-index tetap ada
          // walau layer di-toggle. `pointerEvents: none` di pane: tiap pane punya <svg> sendiri
          // seluas viewport, dan tanpa ini area kosong svg atas menelan klik ke pane di bawahnya.
          // Path interaktif menyalakan lagi pointer-events-nya lewat CSS Leaflet.
          return l.pane
            ? <Pane key={l.key} name={l.pane} style={{ zIndex: l.z, pointerEvents: "none" }}>{geo}</Pane>
            : <Fragment key={l.key}>{geo}</Fragment>;
        })}
        <Sorot refs={refs} data={data} visible={visible} setVisible={setVisible} />
      </MapContainer>

      <div className="absolute left-4 top-4 z-[500] rise">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400">Modul 03 · GIS</p>
        <h2 className="font-display text-3xl font-extrabold drop-shadow-lg">Peta Progres Semarang</h2>
      </div>

      <div className="absolute right-4 top-4 z-[500] w-64 rounded-md border border-border bg-[#131B29]/95 p-4 backdrop-blur" data-testid="layer-panel">
        <p className="flex items-center gap-2 font-display text-lg font-bold"><Layers className="h-4 w-4 text-sky-400" />Layer</p>
        <div className="mt-3 space-y-2">
          {LAYERS.map((l) => (
            <label key={l.key} className="flex cursor-pointer items-center gap-3 text-sm">
              <input type="checkbox" checked={visible[l.key]} onChange={(e) => setVisible((s) => ({ ...s, [l.key]: e.target.checked }))} className="h-4 w-4 accent-sky-500" data-testid={`layer-toggle-${l.key}`} />
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: l.color }} />
              <span className="flex-1">{l.label}</span>
              <span className="font-mono text-[10px] text-slate-500" data-testid={`layer-count-${l.key}`}>{data[l.key]?.features.length ?? "…"}</span>
            </label>
          ))}
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">Warna Grid</p>
        <div className="mt-2 grid grid-cols-2 gap-1 rounded-md bg-[#0B0F17] p-1">
          {["2d", "3d"].map((g) => (
            <button key={g} onClick={() => setGridBy(g)} data-testid={`grid-color-${g}`} className={`rounded py-1 text-xs font-semibold transition-colors ${gridBy === g ? "bg-sky-500 text-[#0B0F17]" : "text-slate-400 hover:text-slate-100"}`}>Status {g.toUpperCase()}</button>
          ))}
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">Basemap</p>
        <div className="mt-2 grid grid-cols-3 gap-1 rounded-md bg-[#0B0F17] p-1">
          {Object.entries(BASEMAPS).map(([k, b]) => (
            <button key={k} onClick={() => setBase(k)} data-testid={`basemap-${k}`} className={`rounded py-1 text-xs font-semibold transition-colors ${base === k ? "bg-sky-500 text-[#0B0F17]" : "text-slate-400 hover:text-slate-100"}`}>{b.label}</button>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 left-4 z-[500] rounded-md border border-border bg-[#131B29]/95 p-4 text-xs text-slate-300 backdrop-blur space-y-1.5" data-testid="map-legend">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">Legenda Status</p>
        <LegendRow c="#10B981" t="Selesai / Disetujui / Terukur / Ada Tgl Akuisisi" />
        <LegendRow c="#0EA5E9" t="Submit / Terupload" />
        <LegendRow c="#F59E0B" t="Dalam Proses" />
        <LegendRow c="#EF4444" t="Revisi / Rusak" />
        <LegendRow c="#64748B" t="Belum / Tanpa Tgl Akuisisi" />
        <p className="pt-2 text-slate-500">● Control Point &nbsp; ◆ Titik BM &nbsp; ┅ Flight Plan</p>
      </div>
    </div>
  );
}
